import { ManagedPaymentMethod } from '../../../util/Managed'
import { PayoutFundingCurrency } from '../../../state/Radom'

export enum PayoutType {
  Crypto = 'Crypto',
  Fiat = 'Fiat'
}

export enum PayoutSteps {
  SelectAssets,
  SelectDetails,
  ReviewAndConfirm
}

export const HEDGED_PAYOUT_DESTINATION_NETWORKS_USDC = [
  'Bitcoin',
  'Ethereum',
  'Solana',
  'Litecoin',
  'Dogecoin',
  'BitcoinCash',
  'Avalanche'
]

export const HEDGED_PAYOUT_DESTINATION_NETWORKS_USDT = [
  ...HEDGED_PAYOUT_DESTINATION_NETWORKS_USDC,
  'Polygon',
  'Tron'
]

// Hedged crypto payouts fund from stablecoins only; volatile funding currencies
// exist for EUR cash-outs and have no hedged destinations.
export const HEDGED_PAYOUT_STABLECOIN_DESTINATIONS: Partial<Record<PayoutFundingCurrency, Record<string, string[]>>> = {
  USDC: {
    USDC: ['Polygon', 'Arbitrum', 'Base', 'Solana'],
    USDT: ['Ethereum', 'Polygon', 'Arbitrum', 'Avalanche', 'Tron', 'Solana']
  },
  USDT: {
    USDC: ['Ethereum', 'Polygon', 'Arbitrum', 'Base', 'Solana'],
    USDT: ['Polygon', 'Arbitrum', 'Avalanche', 'Tron', 'Solana']
  }
}

export interface HedgedDestinationStatus {
  available: boolean
  reason?: string
}

export function getHedgedPayoutDestinationStatus (
  m: ManagedPaymentMethod,
  fundingCurrency: PayoutFundingCurrency
): HedgedDestinationStatus {
  if (m.isTestnet) return { available: false, reason: 'Testnet assets are not available in funded payouts' }
  if (m.isFiat) return { available: false, reason: 'Fiat routes are not available in funded payouts' }

  if (!m.hostChain) {
    const nativeSupported = (fundingCurrency === 'USDT'
      ? HEDGED_PAYOUT_DESTINATION_NETWORKS_USDT
      : HEDGED_PAYOUT_DESTINATION_NETWORKS_USDC
    ).includes(m.name)

    return nativeSupported
      ? { available: true }
      : { available: false, reason: `Not supported for ${fundingCurrency} funding` }
  }

  const networks = HEDGED_PAYOUT_STABLECOIN_DESTINATIONS[fundingCurrency]?.[m.ticker]
  if (!networks) {
    return { available: false, reason: `${m.ticker} is not supported for funded payout destinations` }
  }

  if (!networks.includes(m.hostChain.name)) {
    return { available: false, reason: `${m.ticker} on ${m.hostChain.name} is unavailable for ${fundingCurrency} funding` }
  }

  return { available: true }
}

export function isHedgedPayoutDestination (m: ManagedPaymentMethod, fundingCurrency: PayoutFundingCurrency): boolean {
  return getHedgedPayoutDestinationStatus(m, fundingCurrency).available
}

export const HEDGED_PAYOUT_FUNDING_NETWORK = 'Ethereum'
